/**
 * Squaring of dimension runs — pure geometry, shared by the dimension tool's
 * drawing and the endpoint drags. The wall at the ORIGIN (start) point sets
 * the angle: the run snaps to 90° steps of it, and the other end follows.
 */

import * as THREE from 'three';
import type { FreeRunPointInfo } from './SelectedObjectDrag';

type Point = { x: number; y: number };

/** How far off a 90° step a run may be and still snap (the beam's 90° snap tolerance). */
const SQUARE_SNAP_TOL = (10 * Math.PI) / 180;

/** Foot of `p` on the line through `on` along the unit direction `dir`. */
function footOnLine(p: Point, on: Point, dir: Point): Point {
  const t = (p.x - on.x) * dir.x + (p.y - on.y) * dir.y;
  return { x: on.x + dir.x * t, y: on.y + dir.y * t };
}

/** Whether the run a→b is within the snap tolerance of square to a face along `dir`. */
function nearlySquareTo(a: Point, b: Point, dir: Point): boolean {
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  const dirLen = Math.hypot(dir.x, dir.y);
  if (len < 1e-9 || dirLen < 1e-9) return false;
  return Math.abs(((b.x - a.x) * dir.x + (b.y - a.y) * dir.y) / (len * dirLen)) <= Math.sin(SQUARE_SNAP_TOL);
}

/** The 90° step of the origin's wall sides nearest the run direction `v`, as a unit vector — null beyond the tolerance. */
function squaredDirection(v: Point, sides: readonly Point[]): Point | null {
  const angle = Math.atan2(v.y, v.x);
  let best: { diff: number; dir: Point } | null = null;
  for (const side of sides) {
    const base = Math.atan2(side.y, side.x);
    let delta = angle - base;
    const step = Math.round(delta / (Math.PI / 2));
    delta -= step * (Math.PI / 2);
    const diff = Math.abs(Math.atan2(Math.sin(delta), Math.cos(delta)));
    if (diff <= SQUARE_SNAP_TOL && (!best || diff < best.diff)) {
      const snapped = base + step * (Math.PI / 2);
      best = { diff, dir: { x: Math.cos(snapped), y: Math.sin(snapped) } };
    }
  }
  return best?.dir ?? null;
}

/** Where the ray from `from` along `dir` crosses the line through `on` along `along` (ahead of `from` only). */
function rayHitsLine(from: Point, dir: Point, on: Point, along: Point): Point | null {
  const denom = dir.x * along.y - dir.y * along.x;
  if (Math.abs(denom) < 1e-9) return null;
  const t = ((on.x - from.x) * along.y - (on.y - from.y) * along.x) / denom;
  return t > 0 ? { x: from.x + dir.x * t, y: from.y + dir.y * t } : null;
}

/**
 * Snap a dimension run, the origin's wall setting the angle. `startSides`
 * are the directions of the wall at the start: a face's one, a corner's two,
 * none off the walls. Within the snap tolerance of a 90° step of them:
 *  - an end on a FACE slides along that face to where the snapped run meets it;
 *  - an end on a CORNER stays; a start on a face then slides along its face
 *    to square onto the corner (when `slideStart`);
 *  - a free end follows the snapped direction (same distance along it).
 * A start off the walls instead squares onto the face its end is on.
 * Anything else comes back unchanged.
 */
export function squareDimensionRun(start: THREE.Vector3, startSides: readonly Point[], end: FreeRunPointInfo, slideStart = true): { start: THREE.Vector3; end: THREE.Vector3 } {
  const v = { x: end.point.x - start.x, y: end.point.y - start.y };
  const len = Math.hypot(v.x, v.y);
  const dir = len > 1e-9 ? squaredDirection(v, startSides) : null;
  const at = (p: Point, z: number) => new THREE.Vector3(p.x, p.y, z);

  if (dir) {
    if (end.corner) {
      if (slideStart && startSides.length === 1 && nearlySquareTo(start, end.point, startSides[0])) return { start: at(footOnLine(end.point, start, startSides[0]), start.z), end: end.point };
      return { start, end: end.point };
    }
    if (end.onWall && end.edgeDir) {
      // Only a nearby crossing: a face almost along the run would throw the end far off.
      const hit = rayHitsLine(start, dir, end.point, end.edgeDir);
      if (hit && Math.hypot(hit.x - end.point.x, hit.y - end.point.y) <= len * 0.25) return { start, end: at(hit, end.point.z) };
      return { start, end: end.point };
    }
    const along = v.x * dir.x + v.y * dir.y;
    return { start, end: at({ x: start.x + dir.x * along, y: start.y + dir.y * along }, end.point.z) };
  }

  if (startSides.length === 0 && end.onWall && !end.corner && end.edgeDir && nearlySquareTo(start, end.point, end.edgeDir)) {
    return { start, end: at(footOnLine(start, end.point, end.edgeDir), end.point.z) };
  }
  return { start, end: end.point };
}
